/**
 * @warning If you are adding a new event type you must also add it to
 * src/services/__mocks__/mixpanel.ts so the global test mock stays in sync.
 */

// An allowlist, because the auth-store user carries the Intercom identity hash and a phone.
export interface MixpanelProfile {
  $email?: string
  $name?: string
  organization_name?: string // snake_case on purpose: Rails writes the same profile keys
  role?: string
  default_product?: string
}

export const FEED_UPLOAD_EVENT = 'Feed Uploaded'
export const CREATE_RULE_EVENT = 'Rule Created'
export const UPDATE_RULE_EVENT = 'Rule Updated'
export const CREATE_CHAT_EVENT = 'Chat Created'
export const INSIGHTS_VISIT_EVENT = 'Insights Visit'
export const ASK_AI_VISIT_EVENT = 'Ask AI Visit'
export const AI_BUILDER_EVENT = 'AI Builder'
export const ASK_AI_REQUEST_EVENT = 'Ask AI Request'
export const AI_BUILDER_EDITS_EVENT = 'AI Builder Edits'
export const SELECT_PROMPT_EVENT = 'Select Prompt'
export const CONNECT_TO_SHOPIFY_EVENT = 'Connect to Shopify'
export const REGISTRATION_PAGE_VISIT_EVENT = 'Registration Page Visit'
export const REGISTRATION_PAGE_SIGN_UP_EVENT = 'Registration Page Sign Up'
export const GOOGLE_ANALYTICS_EVENT = 'Google Analytics'
export const INTEGRATION_PAGE_VISIT_EVENT = 'Integration Page Visit'
export const ONBOARDING_PRODUCT_SELECTED_EVENT = 'onboarding_product_selected'
export const AEO_ONBOARDING_RUN_STARTED_EVENT = 'aeo_onboarding_run_started'
export const AEO_ONBOARDING_COMPLETED_EVENT = 'aeo_onboarding_completed'
export const AEO_ONBOARDING_HYDRATION_STARTED_EVENT =
  'aeo_onboarding_hydration_started'
// AEO-tracker (embedded /tracker surface) events. Names mirror the
// @vwam/tracker-ui AnalyticsEvent union; every one carries a `surface` prop.
export const TRACKER_APP_VIEWED_EVENT = 'app_viewed'
export const TRACKER_RUN_SUBMIT_CLICKED_EVENT = 'run_submit_clicked'
export const TRACKER_DASHBOARD_VIEWED_EVENT = 'dashboard_viewed'
export const TRACKER_TAB_SWITCHED_EVENT = 'tab_switched'
export const TRACKER_HISTORY_VIEWED_EVENT = 'history_viewed'
export const TRACKER_RUN_REOPENED_EVENT = 'run_reopened'
export const TRACKER_SCHEDULE_CREATED_EVENT = 'schedule_created'
export const TRACKER_SCHEDULE_DELETED_EVENT = 'schedule_deleted'
export const TRACKER_SIGNUP_CLICKED_EVENT = 'signup_clicked'
export const GUIDED_BUILDER_CREATED_EVENT = 'guided_builder_created'
export const GUIDED_BUILDER_STAGE_VIEWED_EVENT = 'guided_builder_stage_viewed'
export const GUIDED_BUILDER_STAGE_COMPLETED_EVENT =
  'guided_builder_stage_completed'
export const GUIDED_BUILDER_STAGE_SKIPPED_EVENT = 'guided_builder_stage_skipped'
export const GUIDED_BUILDER_GENERATION_ACCEPTED_EVENT =
  'guided_builder_generation_accepted'
export const GUIDED_BUILDER_GENERATION_EDITED_EVENT =
  'guided_builder_generation_edited'
export const GUIDED_BUILDER_GENERATION_RETRIED_EVENT =
  'guided_builder_generation_retried'
export const GUIDED_BUILDER_EXITED_EVENT = 'guided_builder_exited'
export const GUIDED_BUILDER_TEST_STARTED_EVENT = 'guided_builder_test_started'
export const GUIDED_BUILDER_PUBLISH_ATTEMPTED_EVENT =
  'guided_builder_publish_attempted'
export const GUIDED_BUILDER_PUBLISH_COMPLETED_EVENT =
  'guided_builder_publish_completed'
export const TRACKER_SITE_HEALTH_AUDIT_SUBMITTED_EVENT =
  'site_health_audit_submitted'
export const TRACKER_SITE_HEALTH_REPORT_VIEWED_EVENT =
  'site_health_report_viewed'
export const TRACKER_SITE_HEALTH_REPORT_TAB_VIEWED_EVENT =
  'site_health_report_tab_viewed'
export const TRACKER_SITE_HEALTH_FINDING_EXPANDED_EVENT =
  'site_health_finding_expanded'
export const TRACKER_SITE_HEALTH_GUIDANCE_OPENED_EVENT =
  'site_health_guidance_opened'
export const TRACKER_SITE_HEALTH_SUPPORT_ARTICLE_OPENED_EVENT =
  'site_health_support_article_opened'

export type MixpanelEvent =
  | typeof FEED_UPLOAD_EVENT
  | typeof CREATE_RULE_EVENT
  | typeof UPDATE_RULE_EVENT
  | typeof CREATE_CHAT_EVENT
  | typeof INSIGHTS_VISIT_EVENT
  | typeof ASK_AI_VISIT_EVENT
  | typeof AI_BUILDER_EVENT
  | typeof ASK_AI_REQUEST_EVENT
  | typeof AI_BUILDER_EDITS_EVENT
  | typeof SELECT_PROMPT_EVENT
  | typeof CONNECT_TO_SHOPIFY_EVENT
  | typeof REGISTRATION_PAGE_VISIT_EVENT
  | typeof REGISTRATION_PAGE_SIGN_UP_EVENT
  | typeof GOOGLE_ANALYTICS_EVENT
  | typeof INTEGRATION_PAGE_VISIT_EVENT
  | typeof ONBOARDING_PRODUCT_SELECTED_EVENT
  | typeof AEO_ONBOARDING_RUN_STARTED_EVENT
  | typeof AEO_ONBOARDING_COMPLETED_EVENT
  | typeof AEO_ONBOARDING_HYDRATION_STARTED_EVENT
  | typeof TRACKER_APP_VIEWED_EVENT
  | typeof TRACKER_RUN_SUBMIT_CLICKED_EVENT
  | typeof TRACKER_DASHBOARD_VIEWED_EVENT
  | typeof TRACKER_TAB_SWITCHED_EVENT
  | typeof TRACKER_HISTORY_VIEWED_EVENT
  | typeof TRACKER_RUN_REOPENED_EVENT
  | typeof TRACKER_SCHEDULE_CREATED_EVENT
  | typeof TRACKER_SCHEDULE_DELETED_EVENT
  | typeof TRACKER_SIGNUP_CLICKED_EVENT
  | typeof GUIDED_BUILDER_CREATED_EVENT
  | typeof GUIDED_BUILDER_STAGE_VIEWED_EVENT
  | typeof GUIDED_BUILDER_STAGE_COMPLETED_EVENT
  | typeof GUIDED_BUILDER_STAGE_SKIPPED_EVENT
  | typeof GUIDED_BUILDER_GENERATION_ACCEPTED_EVENT
  | typeof GUIDED_BUILDER_GENERATION_EDITED_EVENT
  | typeof GUIDED_BUILDER_GENERATION_RETRIED_EVENT
  | typeof GUIDED_BUILDER_EXITED_EVENT
  | typeof GUIDED_BUILDER_TEST_STARTED_EVENT
  | typeof GUIDED_BUILDER_PUBLISH_ATTEMPTED_EVENT
  | typeof GUIDED_BUILDER_PUBLISH_COMPLETED_EVENT
  | typeof TRACKER_SITE_HEALTH_AUDIT_SUBMITTED_EVENT
  | typeof TRACKER_SITE_HEALTH_REPORT_VIEWED_EVENT
  | typeof TRACKER_SITE_HEALTH_REPORT_TAB_VIEWED_EVENT
  | typeof TRACKER_SITE_HEALTH_FINDING_EXPANDED_EVENT
  | typeof TRACKER_SITE_HEALTH_GUIDANCE_OPENED_EVENT
  | typeof TRACKER_SITE_HEALTH_SUPPORT_ARTICLE_OPENED_EVENT
